// client/src/components/game_client/GameLayout.jsx
import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Chip, Stack, Tooltip, IconButton, alpha } from '@mui/material';
import { Psychology, Public, SwapHoriz } from '@mui/icons-material';
import { useGameSession } from './GameSessionContext';

export default function GameLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isConnected,
    isConnecting,
    isInitializing,
    selectedCharacter,
    isError,
  } = useGameSession();

  const isCabinet = location.pathname.includes('cabinet');
  const isWorld = location.pathname.includes('world');

  const connectionStatus = isConnected
    ? 'connected'
    : isConnecting
      ? 'connecting'
      : isInitializing
        ? 'initializing'
        : isError
          ? 'error'
          : 'disconnected';

  const statusColors = {
    connected: '#22c55e',
    connecting: '#f59e0b',
    initializing: '#3b82f6',
    error: '#ef4444',
    disconnected: '#6b7280',
  };

  const statusLabels = {
    connected: 'В ИГРЕ',
    connecting: 'ПОДКЛЮЧЕНИЕ',
    initializing: 'ИНИЦИАЛИЗАЦИЯ',
    error: 'ОШИБКА',
    disconnected: 'НЕ ПОДКЛЮЧЁН',
  };

  const switchMode = () => {
    if (!isConnected) return;
    if (isCabinet) navigate('/game/world');
    else navigate('/game/cabinet');
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* ГЛОБАЛЬНЫЙ HUD */}
      <Box
        sx={{
          height: 36,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          justifyContent: 'space-between',
          flexShrink: 0,
          zIndex: 1000,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            icon={isCabinet ? <Psychology fontSize="small" /> : <Public fontSize="small" />}
            label={isCabinet ? 'КАБИНЕТ' : 'МИР'}
            sx={{
              height: 22,
              bgcolor: isCabinet ? alpha('#3b82f6', 0.15) : alpha('#ff9800', 0.15),
              color: isCabinet ? '#3b82f6' : '#ff9800',
              fontWeight: 700,
              '& .MuiChip-label': { fontSize: '0.6rem', letterSpacing: 1 },
              '& .MuiChip-icon': { fontSize: 12 },
            }}
          />

          {selectedCharacter && (
            <Tooltip title={`Активный субъект: ${selectedCharacter.name}`}>
              <Chip
                size="small"
                label={selectedCharacter.name}
                color="primary"
                variant="outlined"
                sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.6rem' } }}
              />
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusColors[connectionStatus],
                boxShadow: isConnected ? `0 0 6px ${statusColors.connected}` : 'none',
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: '0.55rem',
                fontFamily: 'monospace',
                color: statusColors[connectionStatus],
                letterSpacing: 1,
                fontWeight: 600,
              }}
            >
              {statusLabels[connectionStatus]}
            </Box>
          </Box>

          <Tooltip title={isCabinet ? 'Перейти в Мир' : 'Перейти в Кабинет'}>
            <span>
              <IconButton
                size="small"
                onClick={switchMode}
                disabled={!isConnected}
                sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <SwapHoriz fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* КОНТЕНТ */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
